import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import { validateManualCandidateForm } from '../../../lib/utils/csvParser';

const EMPTY_FORM = {
  first_name: '',
  linkedin_url: '',
  email: '',
  phone: '+',
};

const formatPhoneInput = (value) => {
  if (!value) return '+';
  const next = value.startsWith('+') ? value : `+${value.replace(/\+/g, '')}`;
  return `+${next.slice(1).replace(/\D/g, '')}`;
};

const translateFieldErrors = (fieldErrors, t) => {
  const next = {};
  Object.entries(fieldErrors || {}).forEach(([field, message]) => {
    if (!message) return;
    if (message === 'First name is required') {
      next[field] = t('candidates.addFirstNameRequired');
    } else if (message === 'LinkedIn URL is required') {
      next[field] = t('candidates.addLinkedinRequired');
    } else if (message.startsWith('Could not recognize "')) {
      const match = message.match(/^Could not recognize "(.+)" as/);
      next[field] = t('candidates.addLinkedinInvalid', { value: match?.[1] || '' });
    } else if (message === 'Email is required') {
      next[field] = t('candidates.addEmailRequired');
    } else if (message.startsWith('Please enter a valid email address')) {
      next[field] = t('candidates.addEmailInvalid');
    } else if (message.startsWith('Please enter a valid phone number')) {
      next[field] = t('candidates.addPhoneInvalid');
    } else {
      next[field] = message;
    }
  });
  return next;
};

const mergeFormValues = (values) => ({
  ...EMPTY_FORM,
  first_name: values?.first_name || '',
  linkedin_url: values?.linkedin_url || '',
  email: values?.email || '',
  phone: values?.phone ? formatPhoneInput(values.phone) : '+',
});

const DemoTestScrapeForm = ({ formId, onValidSubmit, disabled = false, initialValues = null }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => mergeFormValues(initialValues));
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!initialValues) return;
    setForm(mergeFormValues(initialValues));
  }, [
    initialValues?.first_name,
    initialValues?.linkedin_url,
    initialValues?.email,
    initialValues?.phone,
  ]);

  const handleChange = (field) => (event) => {
    if (disabled) return;
    const value = field === 'phone' ? formatPhoneInput(event.target.value) : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (disabled) return;

    const phoneValue = form.phone.trim() === '+' ? '' : form.phone.trim();
    const { isValid, fieldErrors: nextFieldErrors, data } = validateManualCandidateForm({
      ...form,
      phone: phoneValue,
    });

    if (!isValid) {
      setFieldErrors(translateFieldErrors(nextFieldErrors, t));
      return;
    }

    onValidSubmit?.(data);
  };

  return (
    <div className="mx-auto w-full max-w-md rounded-lg border border-border bg-card px-4 py-4 mb-4 shadow-elevation-sm">
      <form
        id={formId}
        onSubmit={handleSubmit}
        noValidate
        className="grid grid-cols-1 gap-y-2.5"
      >
        <Input
          label={t('candidates.addFirstName')}
          value={form.first_name}
          onChange={handleChange('first_name')}
          placeholder={t('candidates.addFirstNamePlaceholder')}
          error={fieldErrors.first_name}
          required
          dense
          disabled={disabled}
        />
        <Input
          label={t('candidates.addLinkedinUrl')}
          value={form.linkedin_url}
          onChange={handleChange('linkedin_url')}
          placeholder={t('candidates.addLinkedinPlaceholder')}
          error={fieldErrors.linkedin_url}
          description={t('candidates.addLinkedinDescription')}
          required
          dense
          disabled={disabled}
        />
        <Input
          type="text"
          inputMode="email"
          autoComplete="email"
          label={t('candidates.addEmail')}
          value={form.email}
          onChange={handleChange('email')}
          placeholder={t('candidates.addEmailPlaceholder')}
          error={fieldErrors.email}
          required
          dense
          disabled={disabled}
        />
        <Input
          type="text"
          inputMode="tel"
          autoComplete="tel"
          label={t('candidates.addPhone')}
          value={form.phone}
          onChange={handleChange('phone')}
          className="font-data"
          error={fieldErrors.phone}
          description={t('candidates.addPhoneDescription')}
          dense
          disabled={disabled}
        />
      </form>
    </div>
  );
};

export default DemoTestScrapeForm;
